import { useState, useEffect, useCallback, useRef } from 'react';
import { battlesApi } from '../api/battles';
import { api } from '../api/axios';

/**
 * Хук для загрузки обложек баттлов.
 * Загружает обложки только для баттлов с coverImageId.
 * 
 * @param {Array} battles - Список баттлов
 * @returns {Object} { coverUrls: { [battleId]: string }, loading: boolean }
 */
export function useBattleCovers(battles = []) {
    const [coverUrls, setCoverUrls] = useState({});
    const [loading, setLoading] = useState(false);
    const loadedBattlesRef = useRef(new Set());

    const loadCover = useCallback(async (battleId, coverImageId) => {
        try {
            // Пробуем загрузить через API с токеном
            const { data: blob } = await api.get(`/files/${coverImageId}/download`, {
                responseType: 'blob',
            });
            
            const blobUrl = URL.createObjectURL(blob);
            setCoverUrls(prev => ({ ...prev, [battleId]: blobUrl }));
        } catch (err) {
            console.warn(`[useBattleCovers] Failed to load cover for battle ${battleId}:`, err.message);
            // Не используем fallback URL т.к. сервер требует авторизацию
            // Оставляем пустой URL - обложка не отобразится
            setCoverUrls(prev => ({ ...prev, [battleId]: null }));
        }
    }, []);

    useEffect(() => {
        if (!battles || battles.length === 0) {
            setCoverUrls({});
            loadedBattlesRef.current.clear();
            return;
        }

        const loadCovers = async () => {
            // Находим баттлы с coverImageId для которых ещё не загружена обложка
            const battlesToLoad = battles.filter(
                b => b.coverImageId && !loadedBattlesRef.current.has(b.id)
            );

            if (battlesToLoad.length === 0) return;

            setLoading(true);

            // Загружаем обложки параллельно (ограничим до 5 одновременных запросов)
            const BATCH_SIZE = 5;
            for (let i = 0; i < battlesToLoad.length; i += BATCH_SIZE) {
                const batch = battlesToLoad.slice(i, i + BATCH_SIZE);
                await Promise.all(
                    batch.map(b => {
                        loadedBattlesRef.current.add(b.id);
                        return loadCover(b.id, b.coverImageId);
                    })
                );
            }
            
            setLoading(false);
        };

        loadCovers();

        // Cleanup при размонтировании
        return () => {
            Object.values(coverUrls).forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [battles, loadCover]);

    return { coverUrls, loading };
}

export default useBattleCovers;
